import {
  DEFAULT_GRID_SIZE,
  KEYS,
  arrayToMap,
  getShortcutKey,
} from "@excalidraw/common";

import { getNonDeletedElements } from "@excalidraw/element";

import { LinearElementEditor } from "@excalidraw/element";

import {
  getSelectedElements,
  getSelectionStateForElements,
} from "@excalidraw/element";

import { syncMovedIndices } from "@excalidraw/element";

import { duplicateElements } from "@excalidraw/element";

import { CaptureUpdateAction } from "@excalidraw/element";

import {
  reapplyAutoLayoutToAllFrames,
  isElementInAutoLayoutFrame,
} from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import { ToolButton } from "../components/ToolButton";
import { DuplicateIcon } from "../components/icons";

import { t } from "../i18n";
import { isSomeElementSelected } from "../scene";

import { register } from "./register";

export const actionDuplicateSelection = register({
  name: "duplicateSelection",
  label: "labels.duplicateSelection",
  icon: DuplicateIcon,
  trackEvent: { category: "element" },
  perform: (elements, appState, formData, app) => {
    if (appState.selectedElementsAreBeingDragged) {
      return false;
    }

    // duplicate selected point(s) if editing a line
    if (appState.editingLinearElement) {
      // TODO: Invariants should be checked here instead of duplicateSelectedPoints()
      try {
        const newAppState = LinearElementEditor.duplicateSelectedPoints(
          appState,
          app.scene,
        );

        return {
          elements,
          appState: newAppState,
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        };
      } catch {
        return false;
      }
    }

    let { duplicatedElements, elementsWithDuplicates } = duplicateElements({
      type: "in-place",
      elements,
      idsOfElementsToDuplicate: arrayToMap(
        getSelectedElements(elements, appState, {
          includeBoundTextElement: true,
          includeElementsInFrames: true,
        }),
      ),
      appState,
      randomizeSeed: true,
      overrides: ({ origElement, origIdToDuplicateId }) => {
        const duplicateFrameId =
          origElement.frameId && origIdToDuplicateId.get(origElement.frameId);

        // Use stored transformation or default offset
        const transformation = appState.lastDuplicateTransformation;
        const offset = transformation
          ? transformation.offset
          : { x: DEFAULT_GRID_SIZE / 2, y: DEFAULT_GRID_SIZE / 2 };

        const overrides: Partial<ExcalidrawElement> = {
          x: origElement.x + offset.x,
          y: origElement.y + offset.y,
          frameId: duplicateFrameId ?? origElement.frameId,
        };

        // Apply rotation if stored
        if (transformation?.rotation && origElement.angle !== undefined) {
          (overrides as any).angle =
            origElement.angle + transformation.rotation;
        }

        return overrides;
      },
    });

    if (app.props.onDuplicate && elementsWithDuplicates) {
      const mappedElements = app.props.onDuplicate(
        elementsWithDuplicates,
        elements,
      );
      if (mappedElements) {
        elementsWithDuplicates = mappedElements;
      }
    }

    // Check if any duplicated elements are in auto layout frames
    // If so, trigger auto layout reapplication to position them correctly
    const elementsMap = arrayToMap(elementsWithDuplicates);
    const hasElementsInAutoLayout = duplicatedElements.some((element) =>
      isElementInAutoLayoutFrame(element, elementsMap),
    );

    if (hasElementsInAutoLayout) {
      elementsWithDuplicates = reapplyAutoLayoutToAllFrames(
        elementsWithDuplicates,
        elementsMap,
      ) as ExcalidrawElement[];
    }

    // Store the transformation for the next duplication
    const transformation = appState.lastDuplicateTransformation;
    const currentOffset = transformation
      ? transformation.offset
      : { x: DEFAULT_GRID_SIZE / 2, y: DEFAULT_GRID_SIZE / 2 };

    return {
      elements: syncMovedIndices(
        elementsWithDuplicates,
        arrayToMap(duplicatedElements),
      ),
      appState: {
        ...appState,
        ...getSelectionStateForElements(
          duplicatedElements,
          getNonDeletedElements(elementsWithDuplicates),
          appState,
        ),
        // Store current offset for next duplication
        lastDuplicateTransformation: {
          offset: currentOffset,
          rotation: transformation?.rotation,
        },
      },
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event) => event[KEYS.CTRL_OR_CMD] && event.key === KEYS.D,
  PanelComponent: ({ elements, appState, updateData }) => (
    <ToolButton
      type="button"
      icon={DuplicateIcon}
      title={`${t("labels.duplicateSelection")} — ${getShortcutKey(
        "CtrlOrCmd+D",
      )}`}
      aria-label={t("labels.duplicateSelection")}
      onClick={() => updateData(null)}
      visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    />
  ),
});
